import analytics from 'commons/tracking/analytics';

// Every event the partner onboarding emits. See `docs/onboarding/tracking.md`.
// The Adobe path shapes these: scalar values without commas or colons, the number that matters
// repeated as `value`, and a fresh object per call because `trackEvent` deletes `value`.

export const ONBOARDING_SOURCE = {
  WELCOME_DIALOG: 'welcome_dialog',
  STEP_DONE_DIALOG: 'step_done_dialog',
  DASHBOARD: 'dashboard',
} as const;

export type OnboardingSource =
  (typeof ONBOARDING_SOURCE)[keyof typeof ONBOARDING_SOURCE];

// Must not throw: these sit in the dialog's lifecycle hooks, so a failing sink would cost the
// partner the dialog itself.
const report = (name: string, props: Record<string, unknown>): void => {
  try {
    analytics.logEvent(name, props);
  } catch (failure) {
    console.warn(`[onboarding] could not report ${name}`, failure);
  }
};

export const trackOnboardingWelcomeImpression = ({
  partnerType,
  openSteps,
}: {
  partnerType: string;
  openSteps: number;
}): void =>
  report('partner_onboarding_welcome_impression', {
    partnerType,
    value: openSteps,
    openStepsCount: openSteps,
  });

export const trackOnboardingWelcomeCtaClick = ({
  nextStep,
}: {
  nextStep: string | null | undefined;
}): void =>
  report('partner_onboarding_welcome_cta_click', {
    nextStep: nextStep || 'none',
  });

export const trackOnboardingChecklistItemClick = ({
  source,
  step,
}: {
  source: OnboardingSource;
  step: string;
}): void => report('partner_onboarding_checklist_item_click', { source, step });

export const trackOnboardingWelcomeDismiss = ({
  via,
  openSteps,
}: {
  via: string;
  openSteps: number;
}): void =>
  report('partner_onboarding_welcome_dismiss', {
    via,
    value: openSteps,
    openStepsCount: openSteps,
  });

// The step done dialog reports the same three shapes as the welcome dialog, under its own names,
// so one dialog's funnel can be read without the other's.

export const trackOnboardingStepDoneImpression = ({
  partnerType,
  openSteps,
}: {
  partnerType: string;
  openSteps: number;
}): void =>
  report('partner_onboarding_step_done_impression', {
    partnerType,
    value: openSteps,
    openStepsCount: openSteps,
  });

export const trackOnboardingStepDoneCtaClick = ({
  nextStep,
}: {
  nextStep: string | null | undefined;
}): void =>
  report('partner_onboarding_step_done_cta_click', {
    nextStep: nextStep || 'none',
  });

export const trackOnboardingStepDoneDismiss = ({
  via,
  openSteps,
}: {
  via: string;
  openSteps: number;
}): void =>
  report('partner_onboarding_step_done_dismiss', {
    via,
    value: openSteps,
    openStepsCount: openSteps,
  });
